import { AlertTriangle, Building2, Calendar, CheckCircle, Eye, Filter, Home, Plus, Search, Users, Wrench, XCircle } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const MAINTENANCE_STAGE_TABS = [
  { key: 'intake', label: '접수대기', statuses: ['pending'] },
  { key: 'processing', label: '처리중', statuses: ['approved', 'in_progress'] },
  { key: 'completed', label: '완료', statuses: ['completed'] },
  { key: 'all', label: '전체', statuses: [] }
];

const Maintenance = () => {
  const { user } = useAuth();
  const [activeStage, setActiveStage] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterWorkType, setFilterWorkType] = useState('all');
  const [selectedPropertyOwner, setSelectedPropertyOwner] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  
  const queryClient = useQueryClient();

  // RMC 사용자 여부 확인
  const isRMCUser = user?.role === 'rental_management_company' || 
                    user?.role === 'rmc_admin' || 
                    user?.role === 'rmc_staff' ||
                    (user?.user_type === 'rmc_user' && user?.role === 'admin');

  // 임대사업자 목록 (RMC 사용자용)
  const { data: propertyOwners } = useQuery({
    queryKey: ['maintenance-property-owners'],
    queryFn: async () => {
      const response = await api.get('/rmc/property-owners');
      console.log('🔍 유지보수 - 임대사업자 목록 응답:', response);
      console.log('🔍 유지보수 - 응답 타입:', typeof response);
      console.log('🔍 유지보수 - 응답이 배열인가?', Array.isArray(response));
      
      // API 응답 구조에 맞게 데이터 추출
      // api.js 인터셉터가 response.data를 반환하므로, /rmc/property-owners는 배열을 직접 반환
      if (response) {
        if (Array.isArray(response.property_owners)) {
          return response.property_owners;
        } else if (response.success && response.data) {
          // 표준 응답 구조: {success: true, data: [...]}
          return Array.isArray(response.data) ? response.data : [];
        } else if (Array.isArray(response)) {
          // 배열이 직접 반환된 경우 (일반적인 경우)
          return response;
        } else if (response.data && Array.isArray(response.data)) {
          // data 필드가 배열인 경우
          return response.data;
        }
      }
      return [];
    },
    enabled: isRMCUser,
    onSuccess: (data) => {
      console.log('🔍 유지보수 - 임대사업자 목록 최종:', data);
      console.log('🔍 유지보수 - 목록 개수:', data?.length || 0);
      if (data && data.length > 0) {
        console.log('🔍 유지보수 - 첫 번째 임대사업자 샘플:', data[0]);
      }
    }
  });

  // 유지보수 요청 목록 가져오기
  const { data: maintenanceData, isLoading, error } = useQuery(['maintenance-new', selectedPropertyOwner], () => {
    const params = {
      per_page: 1000,
      ...(selectedPropertyOwner ? { property_owner_id: selectedPropertyOwner } : {})
    };
    return api.get('/maintenance-new/requests', { params });
  },
    {
      onSuccess: (data) => {
        console.log('🔍 유지보수 요청 목록 로드 성공:', data);
        console.log('🔍 데이터 타입:', typeof data);
        console.log('🔍 requests 속성:', data?.requests);
        console.log('🔍 requests 타입:', typeof data?.requests);
        console.log('🔍 requests가 배열인가?', Array.isArray(data?.requests));
      },
      onError: (error) => {
        console.error('❌ 유지보수 요청 목록 로드 실패:', error);
        console.error('❌ 오류 상세:', error.response?.data);
      }
    }
  );

  const maintenanceRequests = maintenanceData?.requests || [];

  // 작업 시작 뮤테이션
  const approveMutation = useMutation(
    (requestId) => api.patch(`/maintenance-new/requests/${requestId}/approve`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('maintenance-new');
        alert('유지보수 작업이 시작되었습니다.');
      },
      onError: (error) => {
        alert('작업 시작 처리 중 오류가 발생했습니다.');
      }
    }
  );

  const stageIncludesStatus = (status, stageKey) => {
    const stage = MAINTENANCE_STAGE_TABS.find(tab => tab.key === stageKey);
    return !stage || stage.statuses.length === 0 || stage.statuses.includes(status);
  };

  const baseFilteredRequests = maintenanceRequests?.filter(request => {
    const matchesSearch = request.tenant_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         request.building_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         request.unit_number.includes(searchTerm) ||
                         request.repair_content.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesWorkTypeFilter = filterWorkType === 'all' || request.work_type === filterWorkType;
    const matchesPropertyOwner = !selectedPropertyOwner || request.property_owner_id == selectedPropertyOwner;
    return matchesSearch && matchesWorkTypeFilter && matchesPropertyOwner;
  }) || [];

  const stageCounts = MAINTENANCE_STAGE_TABS.reduce((acc, tab) => {
    acc[tab.key] = tab.key === 'all'
      ? baseFilteredRequests.length
      : baseFilteredRequests.filter(request => tab.statuses.includes(request.status)).length;
    return acc;
  }, {});

  // 검색 및 필터링
  const filteredRequests = baseFilteredRequests.filter(request => {
    const matchesStage = stageIncludesStatus(request.status, activeStage);
    const matchesStatusFilter = filterStatus === 'all' || request.status === filterStatus;
    return matchesStage && matchesStatusFilter;
  });

  const activeStageLabel = MAINTENANCE_STAGE_TABS.find(tab => tab.key === activeStage)?.label || '전체';

  const handleSendMaintenanceKakao = async (request) => {
    if (!window.confirm('민원 처리상태 알림톡을 발송 요청할까요?')) return;
    try {
      const response = await api.post(`/message-master/maintenance/${request.id}/status/send`, {});
      alert(`알림톡 요청 완료: ${response.status || response.data?.status || 'READY'}`);
    } catch (error) {
      alert(error.response?.data?.message || '알림톡 요청에 실패했습니다.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 페이지 제목 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">유지보수 관리</h1>
          <p className="mt-1 text-sm text-gray-500">
            유지보수 요청을 관리하세요
          </p>
        </div>
        <button 
          onClick={() => setShowRequestModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <Plus className="mr-2 h-4 w-4" />
          신청 등록
        </button>
      </div>

      {/* 검색 및 필터 */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className={`grid grid-cols-1 gap-4 ${isRMCUser ? 'md:grid-cols-5' : 'md:grid-cols-4'}`}>
          {/* 임대사업자 필터 (RMC 사용자용) */}
          {isRMCUser && (
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Home className="h-5 w-5 text-gray-400" />
              </div>
              <select
                value={selectedPropertyOwner}
                onChange={(e) => setSelectedPropertyOwner(e.target.value)}
                className="pl-10 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              >
                <option value="">전체 임대사업자</option>
                {propertyOwners && Array.isArray(propertyOwners) && propertyOwners.map((owner) => (
                  <option key={owner.id} value={owner.id}>
                    {owner.name}
                    {typeof owner.maintenance_requests_count === 'number'
                      ? ` (${owner.maintenance_requests_count}건 신청)`
                      : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          {/* 검색 */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="임차인명, 건물명, 호수, 내용으로 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>

          {/* 상태 필터 */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Filter className="h-5 w-5 text-gray-400" />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="pl-10 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            >
              <option value="all">전체 상태</option>
              <option value="pending">대기중</option>
              <option value="approved">승인됨</option>
              <option value="in_progress">진행중</option>
              <option value="completed">완료</option>
              <option value="cancelled">취소</option>
            </select>
          </div>

          {/* 작업구분 필터 */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Wrench className="h-5 w-5 text-gray-400" />
            </div>
            <select
              value={filterWorkType}
              onChange={(e) => setFilterWorkType(e.target.value)}
              className="pl-10 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            >
              <option value="all">전체 작업구분</option>
              <option value="planned">계획상의 유지보수</option>
              <option value="requested">임차인 요청에 의한 유지보수</option>
            </select>
          </div>

          {/* 통계 */}
          <div className="text-right">
            <p className="text-sm text-gray-500">총 {filteredRequests.length}건 신청</p>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-2">
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          {MAINTENANCE_STAGE_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => {
                setActiveStage(tab.key);
                setFilterStatus('all');
              }}
              className={`flex items-center justify-between rounded-md px-4 py-3 text-sm font-medium transition-colors ${
                activeStage === tab.key
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span>{tab.label}</span>
              <span className={`rounded-full px-2 py-0.5 text-xs ${
                activeStage === tab.key
                  ? 'bg-white/20 text-white'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {stageCounts[tab.key] || 0}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* 유지보수 요청 목록 */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {filteredRequests.map((request) => (
          <div key={request.id} className="bg-white shadow rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
            {/* 요청 헤더 */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900 truncate">{request.work_type_display}</h3>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  request.status === 'approved' ? 'bg-blue-100 text-blue-800' :
                  request.status === 'in_progress' ? 'bg-indigo-100 text-indigo-800' :
                  request.status === 'completed' ? 'bg-green-100 text-green-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {request.status_display}
                </span>
              </div>
              <div className="flex items-center text-sm text-gray-500">
                <Building2 className="mr-1 h-4 w-4" />
                {request.building_name} - {request.unit_number}
              </div>
            </div>

            {/* 요청 정보 */}
            <div className="px-6 py-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center text-sm text-gray-500">
                  <Users className="mr-2 h-4 w-4" />
                  임차인
                </div>
                <div className="text-sm text-gray-900">
                  {request.tenant_name}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center text-sm text-gray-500">
                  <Calendar className="mr-2 h-4 w-4" />
                  등록일
                </div>
                <div className="text-sm text-gray-900">
                  {new Date(request.registration_date).toLocaleDateString('ko-KR')}
                </div>
              </div>

              {request.scheduled_work_date && (
              <div className="flex items-center justify-between">
                <div className="flex items-center text-sm text-gray-500">
                    <Calendar className="mr-2 h-4 w-4" />
                    작업예정일
                  </div>
                  <div className="text-sm text-gray-900">
                    {new Date(request.scheduled_work_date).toLocaleDateString('ko-KR')}
                  </div>
                </div>
              )}

              {request.maintenance_company && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-sm text-gray-500">
                    <Wrench className="mr-2 h-4 w-4" />
                    유지보수업체
                  </div>
                  <div className="text-sm text-gray-900">
                    {request.maintenance_company}
                    {request.maintenance_company_phone && (
                      <span className="ml-2 text-xs text-gray-500">
                        ({request.maintenance_company_phone})
                      </span>
                    )}
                  </div>
                </div>
              )}

              {request.total_repair_cost > 0 && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-sm text-gray-500">
                    <Wrench className="mr-2 h-4 w-4" />
                    수리비용
                  </div>
                  <div className="text-sm text-gray-900">
                    {request.total_repair_cost.toLocaleString()}원
                    {request.tenant_cost > 0 && (
                      <span className="ml-2 text-xs text-blue-600">
                        (임차인: {request.tenant_cost.toLocaleString()}원)
                      </span>
                    )}
                    {request.landlord_cost > 0 && (
                      <span className="ml-2 text-xs text-green-600">
                        (임대인: {request.landlord_cost.toLocaleString()}원)
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* 요청 내용 미리보기 */}
              <div className="mt-3">
                <p className="text-sm text-gray-600 line-clamp-2">
                  {request.repair_content}
                </p>
              </div>

              {/* SMS 알림 설정 표시 */}
              {request.sms_notification_enabled && (
                <div className="mt-3 p-2 bg-blue-50 rounded-md">
                  <div className="flex items-center text-xs text-blue-600">
                    <AlertTriangle className="mr-1 h-3 w-3" />
                    SMS 알림 설정됨
                  </div>
                </div>
              )}

              {/* 완료 내역 표시 */}
              {request.has_completion && (
                <div className="mt-3 p-2 bg-green-50 rounded-md">
                  <div className="text-xs text-green-600">
                    완료 등록됨
                  </div>
                </div>
              )}

              {request.completion?.admin_advance_payment && (
                <div className="mt-3 p-2 bg-amber-50 rounded-md">
                  <div className="text-xs font-medium text-amber-700">
                    관리자 대납 {request.completion.landlord_cost?.toLocaleString()}원 · {request.completion.advance_billing_status === 'billed' ? '수수료 청구 반영' : '청구대기'}
                  </div>
                </div>
              )}
            </div>

            {/* 액션 버튼 */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <div className="flex space-x-2">
                <button 
                  onClick={() => {
                    setSelectedRequest(request);
                    setShowRequestModal(true);
                  }}
                  className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <Eye className="mr-1 h-4 w-4" />
                  상세보기
                </button>
                
                {request.status === 'pending' && (
                  <button 
                    onClick={() => {
                      if (window.confirm('이 유지보수 작업을 시작하시겠습니까?')) {
                        approveMutation.mutate(request.id);
                      }
                    }}
                    className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <CheckCircle className="mr-1 h-4 w-4" />
                    작업 시작
                  </button>
                )}
                
                {(request.status === 'approved' || request.status === 'in_progress') && !request.has_completion && (
                  <button 
                    onClick={() => {
                      setSelectedRequest(request);
                      setShowCompletionModal(true);
                    }}
                    className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <CheckCircle className="mr-1 h-4 w-4" />
                    작업 완료
                </button>
                )}
                <button
                  onClick={() => handleSendMaintenanceKakao(request)}
                  className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  알림톡
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 빈 상태 */}
      {filteredRequests.length === 0 && (
        <div className="text-center py-12">
          <Wrench className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">유지보수 신청이 없습니다</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || filterStatus !== 'all' || filterWorkType !== 'all'
              ? '검색 조건에 맞는 유지보수 신청이 없습니다.' 
              : `${activeStageLabel} 단계의 유지보수 신청이 없습니다.`}
          </p>
        </div>
      )}

      {/* 유지보수 신청 상세/등록 모달 */}
      {showRequestModal && (
        selectedRequest ? (
          <RequestDetailModal 
            request={selectedRequest}
            onClose={() => {
              setShowRequestModal(false);
              setSelectedRequest(null);
            }}
          />
        ) : (
          <RequestRegistrationModal
            onClose={() => setShowRequestModal(false)}
            onSuccess={() => {
              setShowRequestModal(false);
              queryClient.invalidateQueries('maintenance-new');
            }}
          />
        )
      )}

      {/* 유지보수 완료 등록 모달 */}
      {showCompletionModal && selectedRequest && (
        <CompletionModal 
          request={selectedRequest}
          onClose={() => {
            setShowCompletionModal(false);
            setSelectedRequest(null);
          }}
          onSuccess={() => {
            setShowCompletionModal(false);
            setSelectedRequest(null);
            queryClient.invalidateQueries('maintenance-new');
          }}
        />
      )}
    </div>
  );
};

// 유지보수 신청 상세 모달 컴포넌트
const RequestDetailModal = ({ request, onClose }) => {
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">유지보수 신청 상세</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XCircle className="h-6 w-6" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">건물명</label>
                <p className="mt-1 text-sm text-gray-900">{request.building_name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">세대호수</label>
                <p className="mt-1 text-sm text-gray-900">{request.unit_number}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">임차인명</label>
                <p className="mt-1 text-sm text-gray-900">{request.tenant_name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">등록일</label>
                <p className="mt-1 text-sm text-gray-900">
                  {new Date(request.registration_date).toLocaleDateString('ko-KR')}
                </p>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">수선요청내용</label>
              <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{request.repair_content}</p>
            </div>
            
            {request.scheduled_work_date && (
              <div>
                <label className="block text-sm font-medium text-gray-700">작업예정일</label>
                <p className="mt-1 text-sm text-gray-900">
                  {new Date(request.scheduled_work_date).toLocaleDateString('ko-KR')}
                </p>
              </div>
            )}
            
            {request.maintenance_company && (
              <div>
                <label className="block text-sm font-medium text-gray-700">유지보수업체명</label>
                <p className="mt-1 text-sm text-gray-900">
                  {request.maintenance_company}
                  {request.maintenance_company_phone && (
                    <span className="ml-2 text-gray-500">
                      ({request.maintenance_company_phone})
                    </span>
                  )}
                </p>
              </div>
            )}
            
            {request.total_repair_cost > 0 && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">수리비용</label>
                  <p className="mt-1 text-sm text-gray-900">{request.repair_cost?.toLocaleString()}원</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">부가세</label>
                  <p className="mt-1 text-sm text-gray-900">{request.repair_vat?.toLocaleString()}원</p>
                </div>
              </div>
            )}
            
            {request.total_repair_cost > 0 && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">임차인 부담액</label>
                  <p className="mt-1 text-sm text-blue-600 font-medium">
                    {request.tenant_cost?.toLocaleString()}원
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">임대인 부담액</label>
                  <p className="mt-1 text-sm text-green-600 font-medium">
                    {request.landlord_cost?.toLocaleString()}원
                  </p>
                </div>
              </div>
            )}
            
            {request.total_repair_cost > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700">총 수리비용</label>
                <p className="mt-1 text-sm text-gray-900 font-bold">
                  {request.total_repair_cost?.toLocaleString()}원
                </p>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">작업구분</label>
                <p className="mt-1 text-sm text-gray-900">{request.work_type_display}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">SMS 알림</label>
                <p className="mt-1 text-sm text-gray-900">
                  {request.sms_notification_enabled ? '설정됨' : '설정안됨'}
                </p>
              </div>
            </div>

            {request.completion && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <h4 className="text-sm font-semibold text-gray-900">완료 내역</h4>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="block text-gray-500">완료일</span>
                    <span className="font-medium text-gray-900">{request.completion.completion_date}</span>
                  </div>
                  <div>
                    <span className="block text-gray-500">수선업체</span>
                    <span className="font-medium text-gray-900">{request.completion.repair_company}</span>
                  </div>
                  <div>
                    <span className="block text-gray-500">총 수선비용</span>
                    <span className="font-medium text-gray-900">{request.completion.total_repair_cost?.toLocaleString()}원</span>
                  </div>
                  <div>
                    <span className="block text-gray-500">임대인 부담</span>
                    <span className="font-medium text-green-700">{request.completion.landlord_cost?.toLocaleString()}원</span>
                  </div>
                </div>
                <p className="mt-3 whitespace-pre-wrap text-sm text-gray-700">{request.completion.repair_details}</p>
                {request.completion.admin_advance_payment && (
                  <div className="mt-3 rounded-md bg-amber-100 px-3 py-2 text-sm text-amber-800">
                    관리자 대납 · {request.completion.advance_billing_status === 'billed' ? '월별 수수료 청구 반영' : '월별 수수료 청구대기'}
                    {request.completion.advance_note && (
                      <div className="mt-1 text-xs text-amber-700">{request.completion.advance_note}</div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {request.photos && request.photos.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700">사진</label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {request.photos.map((photo, index) => (
                    <img
                      key={index}
                      src={photo}
                      alt={`사진 ${index + 1}`}
                      className="w-full h-32 object-cover rounded-md"
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// 유지보수 완료 등록 모달 컴포넌트
const CompletionModal = ({ request, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    completion_date: new Date().toISOString().split('T')[0],
    repair_details: '',
    repair_company: request.maintenance_company || '',
    repair_cost: 0,
    repair_vat: 0,
    tenant_cost: 0,
    tenant_payment_method: 'immediate',
    landlord_cost: 0,
    admin_advance_payment: false,
    advance_note: ''
  });

  const completionMutation = useMutation(
    (data) => api.post('/maintenance-new/completions', data),
    {
      onSuccess: () => {
        alert('유지보수 완료가 등록되었습니다.');
        onSuccess();
      },
      onError: (error) => {
        alert('완료 등록 중 오류가 발생했습니다.');
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // 부담금액 검증
    const totalCost = Number(formData.repair_cost || 0) + Number(formData.repair_vat || 0);
    if (formData.tenant_cost + formData.landlord_cost !== totalCost) {
      alert('임차인 부담금액과 임대인 부담금액의 합이 총 수선비용과 일치하지 않습니다.');
      return;
    }
    
    completionMutation.mutate({
      ...formData,
      request_id: request.id
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const numericFields = ['repair_cost', 'repair_vat', 'tenant_cost', 'landlord_cost'];
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox'
        ? checked
        : numericFields.includes(name)
          ? parseInt(value, 10) || 0
          : value
    }));
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">유지보수 완료 등록</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XCircle className="h-6 w-6" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">건물명</label>
                <p className="mt-1 text-sm text-gray-900">{request.building_name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">세대호수</label>
                <p className="mt-1 text-sm text-gray-900">{request.unit_number}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">완료일 *</label>
                <input
                  type="date"
                  name="completion_date"
                  value={formData.completion_date}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">수선업체 *</label>
                <input
                  type="text"
                  name="repair_company"
                  value={formData.repair_company}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">수리내역 *</label>
              <textarea
                name="repair_details"
                value={formData.repair_details}
                onChange={handleInputChange}
                required
                rows={3}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">수선비용 *</label>
                <input
                  type="number"
                  name="repair_cost"
                  value={formData.repair_cost}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">수선비부가세 *</label>
                <input
                  type="number"
                  name="repair_vat"
                  value={formData.repair_vat}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">총 수선비용</label>
                <p className="mt-1 text-sm text-gray-900 font-medium">
                  {(formData.repair_cost + formData.repair_vat).toLocaleString()}원
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">임차인부담금액</label>
                <input
                  type="number"
                  name="tenant_cost"
                  value={formData.tenant_cost}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">납부방법</label>
                <select
                  name="tenant_payment_method"
                  value={formData.tenant_payment_method}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="immediate">즉납</option>
                  <option value="billing">청구서 납부</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">임대인부담금액</label>
              <input
                type="number"
                name="landlord_cost"
                value={formData.landlord_cost}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  name="admin_advance_payment"
                  checked={formData.admin_advance_payment}
                  onChange={handleInputChange}
                  disabled={!formData.landlord_cost}
                  className="mt-1 h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
                />
                <span>
                  <span className="block text-sm font-medium text-gray-900">
                    임대인 부담분 관리자 대납
                  </span>
                  <span className="mt-1 block text-xs text-gray-600">
                    체크하면 임대인 부담금액이 월별 수수료 청구의 추가 청구 대기금으로 저장됩니다.
                  </span>
                </span>
              </label>
              {formData.admin_advance_payment && (
                <textarea
                  name="advance_note"
                  value={formData.advance_note}
                  onChange={handleInputChange}
                  rows={2}
                  placeholder="대납 메모"
                  className="mt-3 block w-full rounded-md border border-amber-200 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-primary-500"
                />
              )}
            </div>
            
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={completionMutation.isLoading}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {completionMutation.isLoading ? '등록중...' : '완료 등록'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// 유지보수 신청 등록 모달 컴포넌트
const RequestRegistrationModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    unit_id: '',
    tenant_id: '',
    building_name: '',
    unit_number: '',
    tenant_name: '',
    registration_date: new Date().toISOString().split('T')[0],
    repair_content: '',
    photos: [],
    scheduled_work_date: '',
    maintenance_company: '',
    maintenance_company_phone: '',
    work_type: 'requested',
    sms_notification_enabled: true,
    repair_cost: 0,
    repair_vat: 0,
    tenant_cost: 0,
    landlord_cost: 0,
    vat_separate: false
  });

  const [units, setUnits] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [propertyOwners, setPropertyOwners] = useState([]);
  const [selectedPropertyOwner, setSelectedPropertyOwner] = useState('');
  const [selectedBuildingId, setSelectedBuildingId] = useState('');

  const filteredUnits = useMemo(() => {
    return units.filter(unit => {
      const ownerMatches = !selectedPropertyOwner || String(unit.property_owner_id) === String(selectedPropertyOwner);
      const buildingMatches = !selectedBuildingId || String(unit.building_id) === String(selectedBuildingId);
      return ownerMatches && buildingMatches;
    });
  }, [units, selectedPropertyOwner, selectedBuildingId]);

  const availableBuildings = useMemo(() => {
    const map = new Map();
    units
      .filter(unit => !selectedPropertyOwner || String(unit.property_owner_id) === String(selectedPropertyOwner))
      .forEach(unit => {
        if (unit.building_id && !map.has(unit.building_id)) {
          map.set(unit.building_id, {
            id: unit.building_id,
            name: unit.building_name,
            address: unit.building_address
          });
        }
      });
    return Array.from(map.values());
  }, [units, selectedPropertyOwner]);

  // 세대 목록 조회
  useQuery('units', () => api.get('/maintenance-new/units'), {
    onSuccess: (data) => {
      setUnits(data || []);
    }
  });

  // 임차인 목록 조회
  useQuery('tenants', () => api.get('/maintenance-new/tenants'), {
    onSuccess: (data) => {
      setTenants(data || []);
    }
  });

  // 임대사업자 목록 조회
  useQuery('property-owners', () => api.get('/maintenance-new/property-owners'), {
    onSuccess: (data) => {
      setPropertyOwners(data || []);
    }
  });

  const registrationMutation = useMutation(
    (data) => api.post('/maintenance-new/requests', data),
    {
      onSuccess: () => {
        alert('유지보수 신청이 등록되었습니다.');
        onSuccess();
      },
      onError: (error) => {
        alert('신청 등록 중 오류가 발생했습니다.');
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // 필수 필드 검증
    if (!formData.unit_id || !formData.tenant_id || !formData.building_name || 
        !formData.unit_number || !formData.tenant_name || !formData.repair_content) {
      alert('필수 항목을 모두 입력해주세요.');
      return;
    }
    
    registrationMutation.mutate(formData);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleUnitChange = async (e) => {
    const unitId = e.target.value;
    const selectedUnit = units.find(unit => unit.id == unitId);
    
    if (selectedUnit) {
      setFormData(prev => ({
        ...prev,
        unit_id: unitId,
        building_name: selectedUnit.building_name,
        unit_number: selectedUnit.unit_number,
        tenant_id: '', // 세대 변경 시 임차인 초기화
        tenant_name: ''
      }));
      
      // 해당 세대의 임차인 자동 조회
      if (unitId) {
        try {
          const response = await api.get(`/maintenance-new/units/${unitId}/tenant`);
          if (response.tenant) {
            setFormData(prev => ({
              ...prev,
              tenant_id: response.tenant.id,
              tenant_name: response.tenant.name
            }));
          } else {
            console.log('해당 세대에 활성 계약이 없습니다.');
          }
        } catch (error) {
          console.error('임차인 조회 실패:', error);
        }
      }
    }
  };

  const handleTenantChange = (e) => {
    const tenantId = e.target.value;
    const selectedTenant = tenants.find(tenant => tenant.id == tenantId);
    
    if (selectedTenant) {
      setFormData(prev => ({
        ...prev,
        tenant_id: tenantId,
        tenant_name: selectedTenant.name
      }));
    }
  };

  const handlePropertyOwnerChange = (e) => {
    const ownerId = e.target.value;
    setSelectedPropertyOwner(ownerId);
    setSelectedBuildingId('');
    setFormData(prev => ({
      ...prev,
      unit_id: '',
      building_name: '',
      unit_number: '',
      tenant_id: '',
      tenant_name: ''
    }));
  };

  const handleBuildingChange = (e) => {
    const buildingId = e.target.value;
    const selectedBuilding = availableBuildings.find(building => String(building.id) === String(buildingId));
    setSelectedBuildingId(buildingId);
    setFormData(prev => ({
      ...prev,
      unit_id: '',
      building_name: selectedBuilding?.name || '',
      unit_number: '',
      tenant_id: '',
      tenant_name: ''
    }));
  };

  const handleCostChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : (name.includes('cost') || name.includes('vat') ? parseInt(value) || 0 : value);
    
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: newValue
      };
      
      // 수리비용이나 부가세가 변경되면 총 비용 재계산
      if (name === 'repair_cost' || name === 'repair_vat') {
        const totalCost = (updated.repair_cost || 0) + (updated.repair_vat || 0);
        const currentTenantCost = updated.tenant_cost || 0;
        const currentLandlordCost = updated.landlord_cost || 0;
        
        // 현재 임차인/임대인 부담액의 합이 총 비용과 다르면 자동 조정
        if (currentTenantCost + currentLandlordCost !== totalCost) {
          // 기본적으로 임차인 부담 100%로 설정
          updated.tenant_cost = totalCost;
          updated.landlord_cost = 0;
        }
      }
      
      return updated;
    });
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">유지보수 신청 등록</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XCircle className="h-6 w-6" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* 임대사업자 선택 (RMC 사용자만 표시, 2개 이상일 때만) */}
            {propertyOwners.length > 1 && (
              <div>
                <label className="block text-sm font-medium text-gray-700">임대사업자 선택</label>
                <select
                  value={selectedPropertyOwner}
                  onChange={handlePropertyOwnerChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">전체 임대사업자</option>
                  {propertyOwners.map(owner => (
                    <option key={owner.id} value={owner.id}>
                      {owner.company_name || owner.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">건물명 *</label>
                <select
                  value={selectedBuildingId}
                  onChange={handleBuildingChange}
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">건물을 선택하세요</option>
                  {availableBuildings.map(building => (
                    <option key={building.id} value={building.id}>
                      {building.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">호실 *</label>
                <select
                  name="unit_id"
                  value={formData.unit_id}
                  onChange={handleUnitChange}
                  required
                  disabled={!selectedBuildingId}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
                >
                  <option value="">호실을 선택하세요</option>
                  {filteredUnits.map(unit => (
                    <option key={unit.id} value={unit.id}>
                      {unit.unit_number}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">임차인명 *</label>
                {formData.tenant_name ? (
                  <div className="mt-1 p-2 bg-gray-50 border border-gray-300 rounded-md text-sm text-gray-900">
                    {formData.tenant_name}
                    <span className="ml-2 text-xs text-gray-500">(자동 선택됨)</span>
                  </div>
                ) : (
                  <div className="mt-1 p-2 bg-yellow-50 border border-yellow-300 rounded-md text-sm text-yellow-800">
                    건물과 호수를 선택하면 임차인이 자동으로 선택됩니다
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">세대호수 *</label>
                <input
                  type="text"
                  name="unit_number"
                  value={formData.unit_number}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">등록일 *</label>
                <input
                  type="date"
                  name="registration_date"
                  value={formData.registration_date}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">수선요청내용 *</label>
              <textarea
                name="repair_content"
                value={formData.repair_content}
                onChange={handleInputChange}
                required
                rows={3}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="수선이 필요한 내용을 자세히 입력해주세요"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">작업예정일</label>
                <input
                  type="date"
                  name="scheduled_work_date"
                  value={formData.scheduled_work_date}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">유지보수업체명</label>
                <input
                  type="text"
                  name="maintenance_company"
                  value={formData.maintenance_company}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">유지보수업체 연락처</label>
                <input
                  type="text"
                  name="maintenance_company_phone"
                  value={formData.maintenance_company_phone}
                  onChange={handleInputChange}
                  placeholder="010-1234-5678"
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">수리비용 (원)</label>
                <input
                  type="number"
                  name="repair_cost"
                  value={formData.repair_cost}
                  onChange={handleCostChange}
                  min="0"
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">부가세 (원)</label>
                <input
                  type="number"
                  name="repair_vat"
                  value={formData.repair_vat}
                  onChange={handleCostChange}
                  min="0"
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">총 수리비용</label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-300 rounded-md text-sm font-medium text-gray-900">
                  {(formData.repair_cost + formData.repair_vat).toLocaleString()}원
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">임차인 부담액 (원)</label>
                <input
                  type="number"
                  name="tenant_cost"
                  value={formData.tenant_cost}
                  onChange={handleCostChange}
                  min="0"
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
                <p className="mt-1 text-xs text-blue-600">※ 청구서에 자동 청구됩니다</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">임대인 부담액 (원)</label>
                <input
                  type="number"
                  name="landlord_cost"
                  value={formData.landlord_cost}
                  onChange={handleCostChange}
                  min="0"
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
                <p className="mt-1 text-xs text-green-600">※ 수익비용 관리에 포함됩니다</p>
              </div>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                name="vat_separate"
                checked={formData.vat_separate}
                onChange={handleCostChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-700">
                부가세 별도 계산 (체크 시 부가세를 별도로 계산)
              </label>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">작업구분 *</label>
                <select
                  name="work_type"
                  value={formData.work_type}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="requested">임차인 요청에 의한 유지보수</option>
                  <option value="planned">계획상의 유지보수</option>
                </select>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="sms_notification_enabled"
                  checked={formData.sms_notification_enabled}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-700">
                  세입자에게 작업계획알림(SMS) 발송
                </label>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={registrationMutation.isLoading}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {registrationMutation.isLoading ? '등록중...' : '신청 등록'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Maintenance;
